import React, { useState } from 'react';
import { UserPlus, Plus } from 'lucide-react';
import { addGuest } from '../api';
import type { Guest } from '../types';

interface AddGuestProps {
  onGuestAdded: (guest: Guest) => void;
  eventId: string;
  currentGuestCount: number;
  guestLimit: number;
}

export default function AddGuest({ onGuestAdded, eventId, currentGuestCount, guestLimit }: AddGuestProps) {
  const [name, setName] = useState('');
  const [table, setTable] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const isAtLimit = currentGuestCount >= guestLimit;

  // The event type is explicitly set to React.FormEvent<HTMLFormElement> for type safety.
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    // This is the most important line. It prevents the browser from reloading the page
    // on form submission. If a redirect is still happening, check for nested <form> tags
    // in the parent component.
    e.preventDefault();
    
    // Check guest limit before attempting to add
    if (isAtLimit) {
      setError(`You have reached your maximum limit of ${guestLimit} guests for this event`);
      return;
    }
    
    if (!name.trim() || !table.trim()) {
      setError('Please fill in all fields');
      return;
    }

    const tableNumber = parseInt(table, 10);
    if (isNaN(tableNumber) || tableNumber < 1) {
      setError('Please enter a valid table number');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // The API call to add the guest
      const response = await addGuest({
        name: name.trim(),
        table: tableNumber,
        eventId: eventId,
      });

      if (response.success && response.guest) {
        // This callback updates the parent component's state, causing the UI to re-render
        // with the new guest, without a page reload.
        onGuestAdded(response.guest);
        
        // Reset form fields for the next entry
        setName('');
        setTable('');
        setError('');
      } else {
        // Handle API errors returned from the server
        setError(response.error || 'An unknown error occurred');
      }
    } catch (err) {
      // Handle network errors or other exceptions during the fetch
      setError('Failed to connect to the server. Please try again.');
      console.error("Add guest error:", err);
    } finally {
      // Ensure the loading state is always turned off
      setIsLoading(false);
    }
  };

  return (
    <div className="card">
      <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <UserPlus size={24} />
        Add New Guest
      </h2>

      {isAtLimit && (
        <div style={{
          padding: '1rem',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          marginBottom: '1rem',
          color: '#c33',
          fontWeight: '600'
        }}>
          Guest limit reached ({guestLimit}/{guestLimit}). Delete a guest to add more.
        </div>
      )}

      {/* The onSubmit handler is attached to the form element itself. */}
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Guest Name</label>
            <input
              id="name"
              type="text"
              className="input"
              placeholder="Enter guest name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading || isAtLimit}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="table">Table Number</label>
            <input
              id="table"
              type="number"
              className="input"
              placeholder="Enter table number"
              value={table}
              onChange={(e) => setTable(e.target.value)}
              min="1"
              disabled={isLoading || isAtLimit}
              required
            />
          </div>
        </div>

        {error && (
          <div className="error-box">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn"
          disabled={isLoading || isAtLimit}
          style={{ width: '100%' }}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" style={{ display: 'inline-block', marginRight: '8px' }}></div>
              Adding Guest...
            </>
          ) : isAtLimit ? (
            <>
              <Plus size={20} style={{ marginRight: '8px' }} />
              Guest Limit Reached ({guestLimit}/{guestLimit})
            </>
          ) : (
            <>
              <Plus size={20} style={{ marginRight: '8px' }} />
              Add Guest
            </>
          )}
        </button>
      </form>
    </div>
  );
}